import React, { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import ReCAPTCHA from 'react-google-recaptcha'
import {
  EnvelopeIcon,
  PhoneIcon,
  PaperAirplaneIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

// Components
import SEO from '@components/common/SEO'
import { Button } from '@components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/Card'

// Types
import type { FC } from 'react'

interface ContactFormData {
  name: string
  email: string
  message: string
}

const ContactPage: FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitSuccess(false)
    setSubmitError('')

    try {
      // Verify reCAPTCHA
      const recaptchaValue = recaptchaRef.current?.getValue()
      if (!recaptchaValue) {
        setSubmitError('กรุณายืนยันว่าคุณไม่ใช่โปรแกรมอัตโนมัติ')
        setIsSubmitting(false)
        return
      }

      // Send to backend API
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:7004'}/api/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken: recaptchaValue
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setSubmitSuccess(true)
        setFormData({ name: '', email: '', message: '' })
        recaptchaRef.current?.reset()
        setTimeout(() => setSubmitSuccess(false), 5000)
      } else {
        setSubmitError(data.message || 'เกิดข้อผิดพลาดในการส่งข้อความ')
      }
    } catch (error) {
      console.error('Contact form error:', error)
      setSubmitError('เกิดข้อผิดพลาดในการส่งข้อความ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {/* SEO */}
      <SEO
        title="ติดต่อเรา - ไทยเซ็กคิวริตี้ไดเรกทอรี่"
        description="ติดต่อทีมงานไทยเซ็กคิวริตี้ไดเรกทอรี่"
      />

      {/* Contact Section */}
      <section className="min-h-screen py-16 md:py-20 bg-gradient-to-br from-neutral-50 to-primary-50">
        <div className="container-wrapper">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
                ติดต่อเรา
              </h1>
              <p className="text-lg text-neutral-600 mb-8">
                มีคำถามหรือข้อสงสัย? ส่งข้อความถึงเราได้เลย
              </p>

              {/* Contact Info */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-neutral-600">
                <a
                  href="mailto:info@koder3.com"
                  className="flex items-center gap-2 hover:text-primary-600 transition-colors"
                >
                  <EnvelopeIcon className="w-5 h-5" />
                  <span>info@koder3.com</span>
                </a>
                <span className="hidden sm:inline text-neutral-300">|</span>
                <a
                  href="tel:028215799"
                  className="flex items-center gap-2 hover:text-primary-600 transition-colors"
                >
                  <PhoneIcon className="w-5 h-5" />
                  <span>02-821-5799</span>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle>ส่งข้อความถึงเรา</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
                      ชื่อ-นามสกุล *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="กรอกชื่อของคุณ"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                      อีเมล *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="example@email.com"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-2">
                      ข้อความ *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                      placeholder="รายละเอียดที่ต้องการติดต่อ..."
                    />
                  </div>

                  {/* reCAPTCHA */}
                  <div className="flex justify-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                    />
                  </div>

                  {/* Success Message */}
                  {submitSuccess && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700"
                    >
                      <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                      <span>ส่งข้อความเรียบร้อยแล้ว</span>
                    </motion.div>
                  )}

                  {/* Error Message */}
                  {submitError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
                    >
                      <span>{submitError}</span>
                    </motion.div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    fullWidth
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    loadingText="กำลังส่ง..."
                    leftIcon={!isSubmitting && <PaperAirplaneIcon className="w-5 h-5" />}
                  >
                    ส่งข้อความ
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default ContactPage
